import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Bell, BellOff, Trash2, RefreshCw, ArrowUp, ArrowDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, EmptyState } from "../components/ui";
import { AlertDialog } from "../components/Dialogs";
import { api } from "../lib/api";
import { usd, shortDate } from "../lib/format";
import { useTranslation } from "react-i18next";

export default function Alerts() {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const load = (silent) => {
    if (!silent) setLoading(true);
    api.alerts().then((r) => setAlerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useRefreshOnReturn(() => load(true));

  const toggle = async (a) => {
    try { await api.updateAlert(a.id, { active: !a.active }); load(); }
    catch { toast.error("Failed to update"); }
  };
  const del = async (id) => {
    try { await api.deleteAlert(id); toast.success("Alert deleted"); load(); }
    catch { toast.error("Failed to delete"); }
  };
  const checkNow = async () => {
    setChecking(true);
    try { await api.checkAlerts(); toast.success("Checked all alerts against latest prices"); load(); }
    catch { toast.error("Check failed"); } finally { setChecking(false); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("alerts.subtitle")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("alerts.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="check-alerts-btn" className="btn-outline" onClick={checkNow} disabled={checking}>
            <RefreshCw size={16} className={checking ? "animate-spin" : ""} /> {t("alerts.checkNow")}
          </button>
          <button data-testid="new-alert-btn" className="btn-primary" onClick={() => setOpen(true)}><Plus size={18} /> {t("alerts.newAlert")}</button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : alerts.length === 0 ? (
        <EmptyState testid="alerts-empty" title={t("alerts.emptyTitle")} description={t("alerts.emptyDesc")}
          action={<button className="btn-primary" onClick={() => setOpen(true)}><Plus size={18} /> {t("alerts.newAlert")}</button>} />
      ) : (
        <div className="space-y-3" data-testid="alerts-list">
          {alerts.map((a, idx) => (
            <Panel key={a.id} delay={idx * 0.04} className={`card p-5 flex items-center justify-between ${a.triggered ? "border-emerald-200 bg-emerald-50/30" : ""}`} testid={`alert-row-${a.id}`}>
              <div className="flex items-center gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${a.condition === "above" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                  {a.condition === "above" ? <ArrowUp size={20} /> : <ArrowDown size={20} />}
                </div>
                <div>
                  <Link to={`/instrument/${a.ticker}`} className="font-heading font-semibold tracking-tight hover:text-brand-600">{a.ticker}</Link>
                  <div className="text-sm text-slate-500">
                    {a.condition === "above" ? t("dialogs.priceAbove") : t("dialogs.priceBelow")} <span className="tabular">{usd(a.target_price)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {a.triggered && (
                  <span className="pill bg-emerald-100 text-emerald-700 flex items-center gap-1" data-testid={`alert-triggered-${a.id}`}>
                    <CheckCircle2 size={13} /> {t("alerts.triggered")} {a.triggered_at ? `· ${shortDate(a.triggered_at)}` : ""}
                  </span>
                )}
                <button data-testid={`alert-toggle-${a.id}`} onClick={() => toggle(a)} className={`p-2 rounded-lg transition-colors ${a.active ? "text-brand-500 hover:bg-brand-50" : "text-slate-300 hover:bg-slate-100"}`} title={a.active ? t("alerts.active") : t("alerts.paused")}>
                  {a.active ? <Bell size={18} /> : <BellOff size={18} />}
                </button>
                <button data-testid={`alert-delete-${a.id}`} onClick={() => del(a.id)} className="p-2 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 transition-colors"><Trash2 size={18} /></button>
              </div>
            </Panel>
          ))}
        </div>
      )}

      <AlertDialog open={open} onClose={() => setOpen(false)} onDone={load} />
    </div>
  );
}
